import {
  ArrowRight,
  BookOpen,
  PencilRuler,
  CircuitBoard,
  CheckCircle2,
  Users,
  Video,
  Sparkles,
  Award,
  Phone,
  Mail,
} from "lucide-react";
import { COMPANY, GOOGLE_FORMS, IMG } from "@/lib/constants";
import { CTASection } from "@/components/CTASection";
import { SectionHeading, EyebrowLabel } from "@/components/SectionHeading";

const trainingImage = IMG.multimeter;

const curriculum = [
  {
    icon: CircuitBoard,
    title: "Single Line Diagrams (SLD)",
    desc: "Read, interpret & build SLDs for industrial power distribution.",
  },
  {
    icon: PencilRuler,
    title: "AutoCAD Practical Training",
    desc: "Hands-on CAD for GA, wiring diagrams and schematics.",
  },
  {
    icon: BookOpen,
    title: "MCC, PCC, PMCC Panel Design",
    desc: "End-to-end panel design with real industry templates.",
  },
  {
    icon: Sparkles,
    title: "Control Circuits & Projects",
    desc: "Real-time projects including control schematics and automation.",
  },
];

const demoLearn = [
  "Introduction to MCC, PCC, and PMCC Panels",
  "Basics of Electrical Panel Design",
  "Understanding SLD (Single Line Diagram)",
  "Real-time Industry Practices",
  "Career Opportunities in Electrical Design",
];

const audience = [
  "Final Year Electrical Engineering Students",
  "Diploma (EEE) Students",
  "Job Seekers & Freshers in Electrical Field",
];

const highlights = [
  { icon: Video, label: "Live Online Session" },
  { icon: Users, label: "Industry-Oriented Teaching" },
  { icon: Sparkles, label: "Interactive Q&A" },
  { icon: Award, label: "Limited Seats" },
];

export default function TrainingPage() {
  const demoHref = GOOGLE_FORMS.demoClass.link || "/contact";

  return (
    <div data-testid="training-page" className="pt-20">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-28">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-14 items-center">
            <div>
              <EyebrowLabel>Training Program</EyebrowLabel>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter text-zinc-950 leading-[1.05]">
                Practical Electrical <br />
                <span className="text-[#EAB308]">Design Training.</span>
              </h1>
              <p className="mt-8 max-w-xl text-lg text-zinc-600 leading-relaxed">
                Become a skilled Electrical Design Engineer by learning SLDs,
                AutoCAD, and real-time projects — structured for industry
                readiness.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href={demoHref}
                  target={GOOGLE_FORMS.demoClass.link ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid="training-hero-cta"
                  className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-[#FACC15] text-zinc-950 px-7 py-4 font-bold uppercase tracking-wide text-sm border-2 border-zinc-950"
                >
                  Join Free Demo Class
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href={`tel:${COMPANY.phone}`}
                  data-testid="training-call"
                  className="inline-flex items-center gap-2 bg-transparent hover:bg-zinc-950 hover:text-white text-zinc-950 px-7 py-4 font-bold uppercase tracking-wide text-sm border-2 border-zinc-950"
                >
                  <Phone className="h-4 w-4" />
                  {COMPANY.phoneDisplay}
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 h-28 w-28 bg-[#EAB308] z-0" />
              <img
                src={trainingImage}
                alt="Practical electrical training"
                className="relative z-10 w-full h-[420px] md:h-[520px] object-cover"
              />
              <div className="absolute -bottom-4 -left-4 h-28 w-28 border-4 border-zinc-950 z-0" />
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="relative bg-white overflow-hidden">
        {/* Subtle decorative background */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.03] pointer-events-none">
          <img src={IMG.blueprintBg} alt="" className="w-full h-full object-cover" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32 relative z-10">
          <div className="grid lg:grid-cols-[1fr_0.8fr] gap-16 items-center">
            <div>
              <SectionHeading
                eyebrow="Practical Curriculum"
                title="A curriculum shaped by real engineering practice."
              />
              <p className="mt-6 text-zinc-600 max-w-xl leading-relaxed">
                Our training isn't just theoretical. We've distilled years of on-site experience 
                into four comprehensive modules designed to bridge the gap between 
                academic knowledge and industry requirements.
              </p>
            </div>
            
            <div className="hidden lg:block relative">
               <div className="absolute inset-0 bg-[#EAB308]/10 -rotate-3 rounded-2xl" />
               <img 
                 src={IMG.trainingPanel} 
                 alt="Hands-on training" 
                 className="relative rounded-2xl shadow-2xl border-4 border-white object-cover aspect-[16/10] grayscale hover:grayscale-0 transition-all duration-700"
               />
               <div className="absolute -bottom-6 -right-6 bg-zinc-950 text-white p-6 shadow-xl border-l-4 border-[#EAB308]">
                 <div className="text-3xl font-black text-[#EAB308]">100%</div>
                 <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mt-1 whitespace-nowrap">Practical Training</div>
               </div>
            </div>
          </div>

          <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {curriculum.map((c, i) => (
              <div
                key={c.title}
                data-testid={`curriculum-${i}`}
                className="bg-white p-8 rounded-xl border border-zinc-200 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-zinc-50 border border-zinc-100 rounded-lg">
                    <c.icon
                      className="h-6 w-6 text-[#EAB308]"
                      strokeWidth={2}
                    />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                    Module 0{i + 1}
                  </span>
                </div>
                
                <h3 className="mt-6 text-xl font-bold tracking-tight text-zinc-900 leading-snug">
                  {c.title}
                </h3>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                  {c.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Demo */}
      <section className="relative bg-zinc-950 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url('${IMG.engineerTablet}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/95 to-zinc-950/60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-14">
            <div>
              <div className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
                <span className="h-px w-10 bg-[#EAB308]" />
                Free Demo Class
              </div>
              <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.98]">
                Experience practical training <br />
                <span className="text-[#EAB308]">before you enroll.</span>
              </h2>
              <p className="mt-6 text-zinc-400 leading-relaxed max-w-xl">
                Get a real-time introduction to industry-oriented electrical
                panel design and engineering concepts. Built to bridge the gap
                between academic knowledge and industry requirements.
              </p>

              <div className="mt-10 grid grid-cols-2 gap-0 border border-zinc-800">
                {highlights.map((h, i) => (
                  <div
                    key={h.label}
                    className={`p-5 flex items-center gap-3 ${
                      i % 2 === 0 ? "border-r border-zinc-800" : ""
                    } ${i < 2 ? "border-b border-zinc-800" : ""}`}
                  >
                    <h.icon className="h-5 w-5 text-[#EAB308]" />
                    <span className="text-xs font-bold uppercase tracking-wide">
                      {h.label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href={demoHref}
                  target={GOOGLE_FORMS.demoClass.link ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid="training-demo-cta"
                  className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-[#FACC15] text-zinc-950 px-6 py-4 font-bold uppercase tracking-wide text-sm border-2 border-[#EAB308]"
                >
                  Register Now for Free Demo
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="inline-flex items-center gap-2 bg-transparent hover:bg-white hover:text-zinc-950 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm border-2 border-white"
                >
                  <Mail className="h-4 w-4" />
                  Email Us
                </a>
              </div>
            </div>

            <div className="space-y-10">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
                  What You Will Learn
                </div>
                <ul className="mt-5 space-y-3">
                  {demoLearn.map((l) => (
                    <li
                      key={l}
                      className="flex items-start gap-3 text-zinc-200"
                    >
                      <CheckCircle2 className="h-5 w-5 mt-0.5 text-[#EAB308] shrink-0" />
                      <span>{l}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
                  Who Can Join
                </div>
                <ul className="mt-5 space-y-3">
                  {audience.map((l) => (
                    <li
                      key={l}
                      className="flex items-start gap-3 text-zinc-200"
                    >
                      <Users className="h-5 w-5 mt-0.5 text-[#EAB308] shrink-0" />
                      <span>{l}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-l-2 border-[#EAB308] pl-5 text-sm text-zinc-400 leading-relaxed">
                Top-performing participants may get exclusive offers and
                discounts on our full training program with certificates.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Embedded demo form (optional) */}
      {GOOGLE_FORMS.demoClass.embed && (
        <section className="bg-white border-t border-zinc-200">
          <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 py-24">
            <SectionHeading
              align="center"
              eyebrow="Reserve Your Seat"
              title="Fill the form to book your free demo."
            />
            <div className="mt-10 border border-zinc-200">
              <iframe
                title="Demo Class Registration"
                src={GOOGLE_FORMS.demoClass.embed}
                className="w-full"
                style={{ minHeight: "800px", border: 0 }}
              >
                Loading…
              </iframe>
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </div>
  );
}
